import { addImageData } from '../media/addImageData';
import { addSvgMarkup } from '../media/addSvgMarkup';
import {
  getLivePreviewConfig,
  getTransform,
} from '../setup/configureLivePreview';
import { previewStack } from '../setup/previewStack';
import { CONTENT_TYPE_POSTFIX } from '../setup/settings';
import { addComputedFields } from '../transform/addComputedFields';
import { removeEmptyObjects } from '../transform/removeEmptyObjects';
import { PlainObject, visitObjects } from '../helpers/helpers';
import { BuildFieldsResolver } from '../helpers/types';
import { loadBuildOnlyBlocks } from './loadBuildOnlyBlocks';
import { repairMixedUpRichText } from './repairMixedUpRichText';
import { resolveReferences } from './resolveReferences';
import { isUnresolvedReference } from './visitUnresolvedReferences';

export type PageUpdate = (currentPageData: PlainObject) => PlainObject;

interface LoadPageUpdateOptions {
  contentTypeBase: string;
  entryUid: string;
  locale?: string;
}

const fetchDraftEntry = async (
  contentTypeUid: string,
  entryUid: string,
  locale?: string
): Promise<PlainObject | null> => {
  const query = previewStack.ContentType(contentTypeUid).Entry(entryUid);

  if (locale) query.language(locale);

  return query.addParam('include_dimension', 'true').toJSON().fetch();
};

const addBuildFields = (
  entry: PlainObject,
  buildFields?: BuildFieldsResolver
): void => {
  if (!buildFields) return;

  try {
    const fields = buildFields(entry);

    if (fields) entry.fields = fields;
  } catch (error) {
    console.warn('[live-preview] could not build page fields:', error);
  }
};

const dropUnresolvedReferences = (
  rootEntry: PlainObject,
  blockTypeNames: Record<string, string>
): void => {
  const visited = new WeakSet<object>();

  const report = (blockName: string | undefined, reference: PlainObject) =>
    console.warn(
      `[live-preview] ${blockName ?? 'page'}: reference ${reference.uid} (${
        reference._content_type_uid
      }) did not load - it was left out`
    );

  const walk = (node: unknown, blockName: string | undefined): void => {
    if (!node || typeof node !== 'object' || visited.has(node)) return;

    visited.add(node);

    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, blockName));

      return;
    }

    Object.entries(node as PlainObject).forEach(([fieldName, value]) => {
      const fieldBlockName = blockTypeNames[fieldName] ?? blockName;

      if (Array.isArray(value)) {
        const resolved = value.filter((item) => {
          if (!isUnresolvedReference(item)) return true;

          report(fieldBlockName, item);

          return false;
        });

        if (resolved.length !== value.length) {
          (node as PlainObject)[fieldName] = resolved;
        }
      }

      walk((node as PlainObject)[fieldName], fieldBlockName);
    });
  };

  walk(rootEntry, undefined);
};

const toContentTypeBase = (contentTypeUid: unknown): string | undefined => {
  const postfix = `_${CONTENT_TYPE_POSTFIX}`;

  return typeof contentTypeUid === 'string' && contentTypeUid.endsWith(postfix)
    ? contentTypeUid.slice(0, -postfix.length)
    : undefined;
};

const addNestedBuildFields = (
  rootEntry: PlainObject,
  buildFields?: Record<string, BuildFieldsResolver>
): void => {
  if (!buildFields) return;

  visitObjects(rootEntry, (object) => {
    if (object === rootEntry || isUnresolvedReference(object)) return undefined;

    const contentTypeBase = toContentTypeBase(object._content_type_uid);
    const resolver = contentTypeBase && buildFields[contentTypeBase];

    if (resolver) addBuildFields(object, resolver);

    return undefined;
  });
};

export const loadPageUpdate = async ({
  contentTypeBase,
  entryUid,
  locale,
}: LoadPageUpdateOptions): Promise<PageUpdate | null> => {
  const { buildFields, computedFields, bodyBlockTypeNames } =
    getLivePreviewConfig();
  const { convertFieldNames, mergeEntry } = getTransform();
  const contentTypeUid = `${contentTypeBase}_${CONTENT_TYPE_POSTFIX}`;

  const entry = await fetchDraftEntry(contentTypeUid, entryUid, locale);

  if (!entry) return null;

  await resolveReferences(entry, locale);
  await repairMixedUpRichText(entry, contentTypeUid, locale);
  addBuildFields(entry, buildFields?.[contentTypeBase]);
  addNestedBuildFields(entry, buildFields);
  dropUnresolvedReferences(entry, bodyBlockTypeNames);
  addComputedFields(entry, computedFields);

  const previewEntry = removeEmptyObjects(
    convertFieldNames(entry)
  ) as PlainObject;

  await addSvgMarkup(previewEntry);

  const buildOnlyBlocks = await loadBuildOnlyBlocks();

  return (currentPageData) =>
    addImageData(mergeEntry(currentPageData, previewEntry, buildOnlyBlocks));
};
